/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * agora/v1/support.proto
 * OpenAPI spec version: version not set
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import * as axios from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

import type {
  ListSupportAccessGrants2Params,
  ListSupportAccessGrantsParams,
  RpcStatus,
  SupportServiceAssumeSupportRoleBodyBody,
  SupportServiceRevokeSupportAccessGrantBodyBody,
  V1AssumeSupportRoleResponse,
  V1CheckUserSupportRoleRequest,
  V1CheckUserSupportRoleResponse,
  V1ListSupportAccessGrantsResponse,
  V1SupportAccessGrant,
  V1SupportAccessGrantBody,
} from '../models';

/**
 * @summary Returns the active support access grants for a given user.
Used internally to check whether a support staff member currently has
any active support access and what scope/resources they can access.
Optionally scoped to a specific organization or workspace via the parent field.
Returns NOT_FOUND if the user has no active support access grants.
 */
export const checkUserSupportRole = (
  v1CheckUserSupportRoleRequest: V1CheckUserSupportRoleRequest,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1CheckUserSupportRoleResponse>> => {
  return axios.default.post(
    `/agora.v1.SupportService/CheckUserSupportRole`,
    v1CheckUserSupportRoleRequest,
    options,
  );
};

export const getCheckUserSupportRoleMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof checkUserSupportRole>>,
    TError,
    { data: V1CheckUserSupportRoleRequest },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof checkUserSupportRole>>,
  TError,
  { data: V1CheckUserSupportRoleRequest },
  TContext
> => {
  const mutationKey = ['checkUserSupportRole'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof checkUserSupportRole>>,
    { data: V1CheckUserSupportRoleRequest }
  > = props => {
    const { data } = props ?? {};

    return checkUserSupportRole(data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CheckUserSupportRoleMutationResult = NonNullable<
  Awaited<ReturnType<typeof checkUserSupportRole>>
>;
export type CheckUserSupportRoleMutationBody = V1CheckUserSupportRoleRequest;
export type CheckUserSupportRoleMutationError = AxiosError<RpcStatus>;

/**
 * @summary Returns the active support access grants for a given user.
Used internally to check whether a support staff member currently has
any active support access and what scope/resources they can access.
Optionally scoped to a specific organization or workspace via the parent field.
Returns NOT_FOUND if the user has no active support access grants.
 */
export const useCheckUserSupportRole = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof checkUserSupportRole>>,
      TError,
      { data: V1CheckUserSupportRoleRequest },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof checkUserSupportRole>>,
  TError,
  { data: V1CheckUserSupportRoleRequest },
  TContext
> => {
  const mutationOptions = getCheckUserSupportRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Lists support access grants for an organization.
 */
export const listSupportAccessGrants = (
  organization: string,
  params?: ListSupportAccessGrantsParams,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ListSupportAccessGrantsResponse>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/supportAccessGrants`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getListSupportAccessGrantsQueryKey = (
  organization?: string,
  params?: ListSupportAccessGrantsParams,
) => {
  return [
    `/api/v1/organizations/${organization}/supportAccessGrants`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListSupportAccessGrantsQueryOptions = <
  TData = Awaited<ReturnType<typeof listSupportAccessGrants>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  params?: ListSupportAccessGrantsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListSupportAccessGrantsQueryKey(organization, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listSupportAccessGrants>>
  > = ({ signal }) =>
    listSupportAccessGrants(organization, params, { signal, ...axiosOptions });

  return {
    queryKey,
    queryFn,
    enabled: !!organization,
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listSupportAccessGrants>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSupportAccessGrantsQueryResult = NonNullable<
  Awaited<ReturnType<typeof listSupportAccessGrants>>
>;
export type ListSupportAccessGrantsQueryError = AxiosError<RpcStatus>;

export function useListSupportAccessGrants<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  params: undefined | ListSupportAccessGrantsParams,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSupportAccessGrants>>,
          TError,
          Awaited<ReturnType<typeof listSupportAccessGrants>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSupportAccessGrants<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  params?: ListSupportAccessGrantsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSupportAccessGrants>>,
          TError,
          Awaited<ReturnType<typeof listSupportAccessGrants>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSupportAccessGrants<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  params?: ListSupportAccessGrantsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Lists support access grants for an organization.
 */

export function useListSupportAccessGrants<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  params?: ListSupportAccessGrantsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListSupportAccessGrantsQueryOptions(
    organization,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Creates a temporary support access grant for an organization, workspaces, or automations.
The caller must be an organization admin.
Access is time-limited (maximum 30 days) and can be revoked at any time.
 */
export const createSupportAccessGrant = (
  organization: string,
  v1SupportAccessGrantBody: V1SupportAccessGrantBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/supportAccessGrants`,
    v1SupportAccessGrantBody,
    options,
  );
};

export const getCreateSupportAccessGrantMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSupportAccessGrant>>,
    TError,
    { organization: string; data: V1SupportAccessGrantBody },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSupportAccessGrant>>,
  TError,
  { organization: string; data: V1SupportAccessGrantBody },
  TContext
> => {
  const mutationKey = ['createSupportAccessGrant'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSupportAccessGrant>>,
    { organization: string; data: V1SupportAccessGrantBody }
  > = props => {
    const { organization, data } = props ?? {};

    return createSupportAccessGrant(organization, data, axiosOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSupportAccessGrantMutationResult = NonNullable<
  Awaited<ReturnType<typeof createSupportAccessGrant>>
>;
export type CreateSupportAccessGrantMutationBody = V1SupportAccessGrantBody;
export type CreateSupportAccessGrantMutationError = AxiosError<RpcStatus>;

/**
 * @summary Creates a temporary support access grant for an organization, workspaces, or automations.
The caller must be an organization admin.
Access is time-limited (maximum 30 days) and can be revoked at any time.
 */
export const useCreateSupportAccessGrant = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSupportAccessGrant>>,
      TError,
      { organization: string; data: V1SupportAccessGrantBody },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createSupportAccessGrant>>,
  TError,
  { organization: string; data: V1SupportAccessGrantBody },
  TContext
> => {
  const mutationOptions = getCreateSupportAccessGrantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Gets a support access grant.
 */
export const getSupportAccessGrant = (
  organization: string,
  supportAccessGrant: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/supportAccessGrants/${supportAccessGrant}`,
    options,
  );
};

export const getGetSupportAccessGrantQueryKey = (
  organization?: string,
  supportAccessGrant?: string,
) => {
  return [
    `/api/v1/organizations/${organization}/supportAccessGrants/${supportAccessGrant}`,
  ] as const;
};

export const getGetSupportAccessGrantQueryOptions = <
  TData = Awaited<ReturnType<typeof getSupportAccessGrant>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetSupportAccessGrantQueryKey(organization, supportAccessGrant);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getSupportAccessGrant>>
  > = ({ signal }) =>
    getSupportAccessGrant(organization, supportAccessGrant, {
      signal,
      ...axiosOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(organization && supportAccessGrant),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSupportAccessGrant>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSupportAccessGrantQueryResult = NonNullable<
  Awaited<ReturnType<typeof getSupportAccessGrant>>
>;
export type GetSupportAccessGrantQueryError = AxiosError<RpcStatus>;

export function useGetSupportAccessGrant<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  supportAccessGrant: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSupportAccessGrant>>,
          TError,
          Awaited<ReturnType<typeof getSupportAccessGrant>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSupportAccessGrant<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSupportAccessGrant>>,
          TError,
          Awaited<ReturnType<typeof getSupportAccessGrant>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSupportAccessGrant<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Gets a support access grant.
 */

export function useGetSupportAccessGrant<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetSupportAccessGrantQueryOptions(
    organization,
    supportAccessGrant,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Assumes a support role, returning the organization and optional workspace context.
The caller must be an internal employee whitelisted for assuming support roles.
 */
export const assumeSupportRole = (
  organization: string,
  supportAccessGrant: string,
  supportServiceAssumeSupportRoleBodyBody: SupportServiceAssumeSupportRoleBodyBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1AssumeSupportRoleResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/supportAccessGrants/${supportAccessGrant}:assume`,
    supportServiceAssumeSupportRoleBodyBody,
    options,
  );
};

export const getAssumeSupportRoleMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof assumeSupportRole>>,
    TError,
    {
      organization: string;
      supportAccessGrant: string;
      data: SupportServiceAssumeSupportRoleBodyBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof assumeSupportRole>>,
  TError,
  {
    organization: string;
    supportAccessGrant: string;
    data: SupportServiceAssumeSupportRoleBodyBody;
  },
  TContext
> => {
  const mutationKey = ['assumeSupportRole'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof assumeSupportRole>>,
    {
      organization: string;
      supportAccessGrant: string;
      data: SupportServiceAssumeSupportRoleBodyBody;
    }
  > = props => {
    const { organization, supportAccessGrant, data } = props ?? {};

    return assumeSupportRole(
      organization,
      supportAccessGrant,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type AssumeSupportRoleMutationResult = NonNullable<
  Awaited<ReturnType<typeof assumeSupportRole>>
>;
export type AssumeSupportRoleMutationBody =
  SupportServiceAssumeSupportRoleBodyBody;
export type AssumeSupportRoleMutationError = AxiosError<RpcStatus>;

/**
 * @summary Assumes a support role, returning the organization and optional workspace context.
The caller must be an internal employee whitelisted for assuming support roles.
 */
export const useAssumeSupportRole = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof assumeSupportRole>>,
      TError,
      {
        organization: string;
        supportAccessGrant: string;
        data: SupportServiceAssumeSupportRoleBodyBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof assumeSupportRole>>,
  TError,
  {
    organization: string;
    supportAccessGrant: string;
    data: SupportServiceAssumeSupportRoleBodyBody;
  },
  TContext
> => {
  const mutationOptions = getAssumeSupportRoleMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Revokes an active support access grant.
Sets the grant state to REVOKED and records the revocation time.
 */
export const revokeSupportAccessGrant = (
  organization: string,
  supportAccessGrant: string,
  supportServiceRevokeSupportAccessGrantBodyBody: SupportServiceRevokeSupportAccessGrantBodyBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/supportAccessGrants/${supportAccessGrant}:revoke`,
    supportServiceRevokeSupportAccessGrantBodyBody,
    options,
  );
};

export const getRevokeSupportAccessGrantMutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeSupportAccessGrant>>,
    TError,
    {
      organization: string;
      supportAccessGrant: string;
      data: SupportServiceRevokeSupportAccessGrantBodyBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeSupportAccessGrant>>,
  TError,
  {
    organization: string;
    supportAccessGrant: string;
    data: SupportServiceRevokeSupportAccessGrantBodyBody;
  },
  TContext
> => {
  const mutationKey = ['revokeSupportAccessGrant'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeSupportAccessGrant>>,
    {
      organization: string;
      supportAccessGrant: string;
      data: SupportServiceRevokeSupportAccessGrantBodyBody;
    }
  > = props => {
    const { organization, supportAccessGrant, data } = props ?? {};

    return revokeSupportAccessGrant(
      organization,
      supportAccessGrant,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeSupportAccessGrantMutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeSupportAccessGrant>>
>;
export type RevokeSupportAccessGrantMutationBody =
  SupportServiceRevokeSupportAccessGrantBodyBody;
export type RevokeSupportAccessGrantMutationError = AxiosError<RpcStatus>;

/**
 * @summary Revokes an active support access grant.
Sets the grant state to REVOKED and records the revocation time.
 */
export const useRevokeSupportAccessGrant = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeSupportAccessGrant>>,
      TError,
      {
        organization: string;
        supportAccessGrant: string;
        data: SupportServiceRevokeSupportAccessGrantBodyBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeSupportAccessGrant>>,
  TError,
  {
    organization: string;
    supportAccessGrant: string;
    data: SupportServiceRevokeSupportAccessGrantBodyBody;
  },
  TContext
> => {
  const mutationOptions = getRevokeSupportAccessGrantMutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Lists support access grants for an organization.
 */
export const listSupportAccessGrants2 = (
  organization: string,
  workspace: string,
  params?: ListSupportAccessGrants2Params,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1ListSupportAccessGrantsResponse>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants`,
    {
      ...options,
      params: { ...params, ...options?.params },
    },
  );
};

export const getListSupportAccessGrants2QueryKey = (
  organization?: string,
  workspace?: string,
  params?: ListSupportAccessGrants2Params,
) => {
  return [
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants`,
    ...(params ? [params] : []),
  ] as const;
};

export const getListSupportAccessGrants2QueryOptions = <
  TData = Awaited<ReturnType<typeof listSupportAccessGrants2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params?: ListSupportAccessGrants2Params,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getListSupportAccessGrants2QueryKey(organization, workspace, params);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof listSupportAccessGrants2>>
  > = ({ signal }) =>
    listSupportAccessGrants2(organization, workspace, params, {
      signal,
      ...axiosOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(organization && workspace),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof listSupportAccessGrants2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type ListSupportAccessGrants2QueryResult = NonNullable<
  Awaited<ReturnType<typeof listSupportAccessGrants2>>
>;
export type ListSupportAccessGrants2QueryError = AxiosError<RpcStatus>;

export function useListSupportAccessGrants2<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params: undefined | ListSupportAccessGrants2Params,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants2>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSupportAccessGrants2>>,
          TError,
          Awaited<ReturnType<typeof listSupportAccessGrants2>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSupportAccessGrants2<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params?: ListSupportAccessGrants2Params,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants2>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSupportAccessGrants2>>,
          TError,
          Awaited<ReturnType<typeof listSupportAccessGrants2>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useListSupportAccessGrants2<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params?: ListSupportAccessGrants2Params,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Lists support access grants for an organization.
 */

export function useListSupportAccessGrants2<
  TData = Awaited<ReturnType<typeof listSupportAccessGrants2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  params?: ListSupportAccessGrants2Params,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof listSupportAccessGrants2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getListSupportAccessGrants2QueryOptions(
    organization,
    workspace,
    params,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Creates a temporary support access grant for an organization, workspaces, or automations.
The caller must be an organization admin.
Access is time-limited (maximum 30 days) and can be revoked at any time.
 */
export const createSupportAccessGrant2 = (
  organization: string,
  workspace: string,
  v1SupportAccessGrantBody: V1SupportAccessGrantBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants`,
    v1SupportAccessGrantBody,
    options,
  );
};

export const getCreateSupportAccessGrant2MutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof createSupportAccessGrant2>>,
    TError,
    { organization: string; workspace: string; data: V1SupportAccessGrantBody },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof createSupportAccessGrant2>>,
  TError,
  { organization: string; workspace: string; data: V1SupportAccessGrantBody },
  TContext
> => {
  const mutationKey = ['createSupportAccessGrant2'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof createSupportAccessGrant2>>,
    { organization: string; workspace: string; data: V1SupportAccessGrantBody }
  > = props => {
    const { organization, workspace, data } = props ?? {};

    return createSupportAccessGrant2(
      organization,
      workspace,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type CreateSupportAccessGrant2MutationResult = NonNullable<
  Awaited<ReturnType<typeof createSupportAccessGrant2>>
>;
export type CreateSupportAccessGrant2MutationBody = V1SupportAccessGrantBody;
export type CreateSupportAccessGrant2MutationError = AxiosError<RpcStatus>;

/**
 * @summary Creates a temporary support access grant for an organization, workspaces, or automations.
The caller must be an organization admin.
Access is time-limited (maximum 30 days) and can be revoked at any time.
 */
export const useCreateSupportAccessGrant2 = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof createSupportAccessGrant2>>,
      TError,
      {
        organization: string;
        workspace: string;
        data: V1SupportAccessGrantBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof createSupportAccessGrant2>>,
  TError,
  { organization: string; workspace: string; data: V1SupportAccessGrantBody },
  TContext
> => {
  const mutationOptions = getCreateSupportAccessGrant2MutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Gets a support access grant.
 */
export const getSupportAccessGrant2 = (
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.get(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants/${supportAccessGrant}`,
    options,
  );
};

export const getGetSupportAccessGrant2QueryKey = (
  organization?: string,
  workspace?: string,
  supportAccessGrant?: string,
) => {
  return [
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants/${supportAccessGrant}`,
  ] as const;
};

export const getGetSupportAccessGrant2QueryOptions = <
  TData = Awaited<ReturnType<typeof getSupportAccessGrant2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
) => {
  const { query: queryOptions, axios: axiosOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetSupportAccessGrant2QueryKey(
      organization,
      workspace,
      supportAccessGrant,
    );

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getSupportAccessGrant2>>
  > = ({ signal }) =>
    getSupportAccessGrant2(organization, workspace, supportAccessGrant, {
      signal,
      ...axiosOptions,
    });

  return {
    queryKey,
    queryFn,
    enabled: !!(organization && workspace && supportAccessGrant),
    staleTime: 60000,
    refetchOnWindowFocus: false,
    ...queryOptions,
  } as UseQueryOptions<
    Awaited<ReturnType<typeof getSupportAccessGrant2>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetSupportAccessGrant2QueryResult = NonNullable<
  Awaited<ReturnType<typeof getSupportAccessGrant2>>
>;
export type GetSupportAccessGrant2QueryError = AxiosError<RpcStatus>;

export function useGetSupportAccessGrant2<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options: {
    query: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant2>>,
        TError,
        TData
      >
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSupportAccessGrant2>>,
          TError,
          Awaited<ReturnType<typeof getSupportAccessGrant2>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSupportAccessGrant2<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant2>>,
        TError,
        TData
      >
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSupportAccessGrant2>>,
          TError,
          Awaited<ReturnType<typeof getSupportAccessGrant2>>
        >,
        'initialData'
      >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
export function useGetSupportAccessGrant2<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
};
/**
 * @summary Gets a support access grant.
 */

export function useGetSupportAccessGrant2<
  TData = Awaited<ReturnType<typeof getSupportAccessGrant2>>,
  TError = AxiosError<RpcStatus>,
>(
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<ReturnType<typeof getSupportAccessGrant2>>,
        TError,
        TData
      >
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & {
  queryKey: DataTag<QueryKey, TData, TError>;
} {
  const queryOptions = getGetSupportAccessGrant2QueryOptions(
    organization,
    workspace,
    supportAccessGrant,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<
    TData,
    TError
  > & { queryKey: DataTag<QueryKey, TData, TError> };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * @summary Assumes a support role, returning the organization and optional workspace context.
The caller must be an internal employee whitelisted for assuming support roles.
 */
export const assumeSupportRole2 = (
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  supportServiceAssumeSupportRoleBodyBody: SupportServiceAssumeSupportRoleBodyBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1AssumeSupportRoleResponse>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants/${supportAccessGrant}:assume`,
    supportServiceAssumeSupportRoleBodyBody,
    options,
  );
};

export const getAssumeSupportRole2MutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof assumeSupportRole2>>,
    TError,
    {
      organization: string;
      workspace: string;
      supportAccessGrant: string;
      data: SupportServiceAssumeSupportRoleBodyBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof assumeSupportRole2>>,
  TError,
  {
    organization: string;
    workspace: string;
    supportAccessGrant: string;
    data: SupportServiceAssumeSupportRoleBodyBody;
  },
  TContext
> => {
  const mutationKey = ['assumeSupportRole2'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof assumeSupportRole2>>,
    {
      organization: string;
      workspace: string;
      supportAccessGrant: string;
      data: SupportServiceAssumeSupportRoleBodyBody;
    }
  > = props => {
    const { organization, workspace, supportAccessGrant, data } = props ?? {};

    return assumeSupportRole2(
      organization,
      workspace,
      supportAccessGrant,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type AssumeSupportRole2MutationResult = NonNullable<
  Awaited<ReturnType<typeof assumeSupportRole2>>
>;
export type AssumeSupportRole2MutationBody =
  SupportServiceAssumeSupportRoleBodyBody;
export type AssumeSupportRole2MutationError = AxiosError<RpcStatus>;

/**
 * @summary Assumes a support role, returning the organization and optional workspace context.
The caller must be an internal employee whitelisted for assuming support roles.
 */
export const useAssumeSupportRole2 = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof assumeSupportRole2>>,
      TError,
      {
        organization: string;
        workspace: string;
        supportAccessGrant: string;
        data: SupportServiceAssumeSupportRoleBodyBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof assumeSupportRole2>>,
  TError,
  {
    organization: string;
    workspace: string;
    supportAccessGrant: string;
    data: SupportServiceAssumeSupportRoleBodyBody;
  },
  TContext
> => {
  const mutationOptions = getAssumeSupportRole2MutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};

/**
 * @summary Revokes an active support access grant.
Sets the grant state to REVOKED and records the revocation time.
 */
export const revokeSupportAccessGrant2 = (
  organization: string,
  workspace: string,
  supportAccessGrant: string,
  supportServiceRevokeSupportAccessGrantBodyBody: SupportServiceRevokeSupportAccessGrantBodyBody,
  options?: AxiosRequestConfig,
): Promise<AxiosResponse<V1SupportAccessGrant>> => {
  return axios.default.post(
    `/api/v1/organizations/${organization}/workspaces/${workspace}/supportAccessGrants/${supportAccessGrant}:revoke`,
    supportServiceRevokeSupportAccessGrantBodyBody,
    options,
  );
};

export const getRevokeSupportAccessGrant2MutationOptions = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof revokeSupportAccessGrant2>>,
    TError,
    {
      organization: string;
      workspace: string;
      supportAccessGrant: string;
      data: SupportServiceRevokeSupportAccessGrantBodyBody;
    },
    TContext
  >;
  axios?: AxiosRequestConfig;
}): UseMutationOptions<
  Awaited<ReturnType<typeof revokeSupportAccessGrant2>>,
  TError,
  {
    organization: string;
    workspace: string;
    supportAccessGrant: string;
    data: SupportServiceRevokeSupportAccessGrantBodyBody;
  },
  TContext
> => {
  const mutationKey = ['revokeSupportAccessGrant2'];
  const { mutation: mutationOptions, axios: axiosOptions } = options
    ? options.mutation &&
      'mutationKey' in options.mutation &&
      options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey }, axios: undefined };

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof revokeSupportAccessGrant2>>,
    {
      organization: string;
      workspace: string;
      supportAccessGrant: string;
      data: SupportServiceRevokeSupportAccessGrantBodyBody;
    }
  > = props => {
    const { organization, workspace, supportAccessGrant, data } = props ?? {};

    return revokeSupportAccessGrant2(
      organization,
      workspace,
      supportAccessGrant,
      data,
      axiosOptions,
    );
  };

  return { mutationFn, ...mutationOptions };
};

export type RevokeSupportAccessGrant2MutationResult = NonNullable<
  Awaited<ReturnType<typeof revokeSupportAccessGrant2>>
>;
export type RevokeSupportAccessGrant2MutationBody =
  SupportServiceRevokeSupportAccessGrantBodyBody;
export type RevokeSupportAccessGrant2MutationError = AxiosError<RpcStatus>;

/**
 * @summary Revokes an active support access grant.
Sets the grant state to REVOKED and records the revocation time.
 */
export const useRevokeSupportAccessGrant2 = <
  TError = AxiosError<RpcStatus>,
  TContext = unknown,
>(
  options?: {
    mutation?: UseMutationOptions<
      Awaited<ReturnType<typeof revokeSupportAccessGrant2>>,
      TError,
      {
        organization: string;
        workspace: string;
        supportAccessGrant: string;
        data: SupportServiceRevokeSupportAccessGrantBodyBody;
      },
      TContext
    >;
    axios?: AxiosRequestConfig;
  },
  queryClient?: QueryClient,
): UseMutationResult<
  Awaited<ReturnType<typeof revokeSupportAccessGrant2>>,
  TError,
  {
    organization: string;
    workspace: string;
    supportAccessGrant: string;
    data: SupportServiceRevokeSupportAccessGrantBodyBody;
  },
  TContext
> => {
  const mutationOptions = getRevokeSupportAccessGrant2MutationOptions(options);

  return useMutation(mutationOptions, queryClient);
};
